import { create } from "zustand";

/**
 * useMobileIndicatorStore
 *
 * A custom Zustand store for managing the state of the currently selected mobile indicator
 * in the application's mobile views.
 *
 * ## Goals:
 * - Centralize the selection state (`mobileIndicatorKey` and `mobileIndicatorName`) for mobile indicators,
 *   so any component can get or update which indicator is currently active for detailed display.
 * - Provide setter functions (`setMobileIndicatorKey`, `setMobileIndicatorName`) to update
 *   the current mobile indicator in a simple and consistent way throughout the app.
 *
 * ## State:
 * - `mobileIndicatorKey`: (string|null) Holds the unique key or ID of the selected indicator.
 * - `mobileIndicatorName`: (string|null) Holds the display name of the currently selected indicator.
 *
 * ## Actions:
 * - `setMobileIndicatorKey(key)`: Update the selected indicator key.
 * - `setMobileIndicatorName(name)`: Update the selected indicator display name.
 *
 * This store is intended for use in mobile components/pages that need to display indicator
 * details, navigate between indicators, or otherwise react to which indicator is currently selected.
 *
 * @returns {object} Zustand store with state and actions for mobile indicator selection.
 */
const useMobileIndicatorStore = create((set) => ({
  mobileIndicatorKey: null,
  mobileIndicatorName: null,
  setMobileIndicatorName: (name) => set({ mobileIndicatorName: name }),
  setMobileIndicatorKey: (key) => set({ mobileIndicatorKey: key }),
}));

export default useMobileIndicatorStore;
